import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { of, Subject, takeUntil } from 'rxjs';
import { ModalConfig } from 'src/app/_metronic/partials';
import { NgbActiveModal } from 'tabweeb-shared-components';
import { CompanyService } from 'src/app/modules/settings/services/company.service';
import { TranslateService } from '@ngx-translate/core';
import * as XLSX from 'xlsx';

interface ExcelUploadPayload {
  file: File;
  sheetName: string;
  keys: {
    tableKey: string;
    excelKey: string;
  }[];
}

/**
 * Excel Uploader Modal Component
 * 
 * This component provides two modes for handling Excel file uploads:
 * 
 * 1. Payload Mode (default): Returns a payload object with file, sheetName, and key mappings
 *    - Set enablePreviewMode = false (default)
 *    - Returns: { file: File, sheetName: string, keys: { tableKey: string, excelKey: string }[] }
 * 
 * 2. Preview Mode: Processes the Excel data and shows a preview before returning actual values
 *    - Set enablePreviewMode = true
 *    - Returns: Array of processed data objects
 * 
 * Usage:
 * - For payload mode: <app-excel-uploader-modal [target]="file" [modelKeys]="keys" [modalConfig]="config"></app-excel-uploader-modal>
 * - For preview mode: <app-excel-uploader-modal [target]="file" [modelKeys]="keys" [modalConfig]="config" [enablePreviewMode]="true"></app-excel-uploader-modal>
 */
@Component({
  selector: 'app-excel-uploader-modal',
  templateUrl: './excel-uploader-modal.component.html',
  styleUrls: ['./excel-uploader-modal.component.css']
})
export class ExcelUploaderModalComponent implements OnInit, OnDestroy {
  @Input() target: any; // raw binary or File data
  @Input() modelKeys: { title: string; isRequired: boolean; label: string }[] = [];
  @Input() public modalConfig: ModalConfig;
  @Input() enablePreviewMode: boolean = true; // Flag to choose between preview mode and payload mode
  /** When true, only sheet (page) selection is shown — no per-column mapping (e.g. bulk DataImportExport). */
  @Input() sheetSelectionOnly = false;
  @Input() warningMessage: string = ''; // Optional warning message to display

  workbook: XLSX.WorkBook;
  sheetNames: string[] = [];
  selectedSheet: string;
  sheetSelectionError = false;

  headers: string[] = [];
  columnMapping: { [key: string]: string } = {};
  rawData: any[] = []; // For preview mode

  formGroup: FormGroup;
  loading = true;

  refresh = 0;

  // Preview mode properties
  showPreview = false;
  previewData: any[] = [];

  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;
  visiblePages: number = 5;

  private destroy$ = new Subject<void>();

  limitWarning = {
    ar: "الحد الأقصى لعدد المدخلات هو 50,000 عنصر",
    en: "The maximum number of inputs is 50,000 items"
  }

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private companyService: CompanyService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.initExcel();
    this.initForm();
    this.loadCompanyDefault();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCompanyDefault(): void {
    this.companyService.getDefault(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.data?.importRowLimit) {
            this.limitWarning = {
                ar: `الحد الأقصى لعدد المدخلات هو ${res.data.importRowLimit.toLocaleString()} عنصر`,
                en: `The maximum number of inputs is ${res.data.importRowLimit.toLocaleString()} items`
            }
          }
        },
        error: (err) => {
          console.error('Error loading company default:', err);
        }
      });
  }

  initForm(): void {
    if (this.sheetSelectionOnly) {
      this.formGroup = this.fb.group({});
      return;
    }
    const group: { [key: string]: any } = {};
    this.modelKeys.forEach(key => {
      group[key.title] = [null, key.isRequired ? Validators.required : null];
    });
    this.formGroup = this.fb.group(group);
  }

  initExcel() {
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const bstr = e.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        
        this.workbook = workbook;
        this.sheetNames = this.workbook.SheetNames.slice();

        const firstSheet = this.sheetNames[0] ?? '';
        if (this.sheetSelectionOnly) {
          // In sheet-selection mode, force an explicit user choice before import.
          this.selectedSheet = '';
          this.headers = [];
          this.rawData = [];
        } else {
          this.selectedSheet = firstSheet;
          this.loadSheet(this.selectedSheet);
        }

        this.loading = false;
      } catch (error) {
        console.error('Error processing Excel file:', error);
        this.loading = false;
      }
    };
    reader.readAsBinaryString(this.target);
  }


  // Loads a single sheet's data (headers + rows) based on mode
  loadSheet = (sheetName: string) => {
    if (!this.workbook || !sheetName) {
      this.headers = [];
      this.rawData = [];
      return;
    }

    const ws: XLSX.WorkSheet = this.workbook.Sheets[sheetName];
    const data: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

    // First row = headers, subsequent rows = raw data (for preview mode)
    this.headers = (data[0] as string[]) || [];
    
    if (this.enablePreviewMode) {
      this.rawData = data.slice(1);
    } else {
      this.rawData = []; // Clear raw data in payload mode
    }
  }

  // Called whenever dropdown changes; $event is { id: string, value: string }
  onSheetChange(event: { id: string; value: string }) {
    const sheetName = event?.id;
    if (!sheetName) {
      return;
    }

    this.sheetSelectionError = false;
    this.selectedSheet = sheetName;

    if (this.workbook) {
      this.loadSheet(sheetName);
    }

    // Reset any existing mappings so user remaps for new sheet:
    this.formGroup.reset();
    this.columnMapping = {};
    this.refresh += 1;
  }

  getHeaders = (pageNumber: number) => {
    if (pageNumber > 1) return of([]);
    return of(
      this.headers.map(el => ({ id: el, value: el }))
    );
  }

  getSheets = () => {
    return of(
      this.sheetNames.map(el => ({ id: el, value: el }))
    );
  }

  getControl(title: string): FormControl {
    return this.formGroup.get(title) as FormControl;
  }

  close() {
    this.activeModal.dismiss();
  }

  handleSubmit() {
    if (!this.selectedSheet) {
      this.sheetSelectionError = true;
      return;
    }

    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }
    this.columnMapping = this.formGroup.value;
    
    if (this.enablePreviewMode) {
      if (!this.showPreview) {
        this.previewData = this.processData();
        this.updatePagination();
        this.showPreview = true;
      } else {
        this.activeModal.close(this.processData());
      }
    } else {
      this.activeModal.close(this.createPayload());
    }
  }


  createPayload(): ExcelUploadPayload {
    // Create the keys mapping array
    const keys = this.modelKeys.map(key => ({
      tableKey: key.title,
      excelKey: this.columnMapping[key.title] || ''
    }));

    return {
      file: this.target,
      sheetName: this.selectedSheet,
      keys: keys
    };
  }

  // Preview mode methods
  updatePagination(): void {
    this.totalPages = Math.ceil(this.previewData.length / this.itemsPerPage);
    this.currentPage = 1; // Reset to first page
  }

  getPaginatedData(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.previewData.slice(startIndex, startIndex + this.itemsPerPage);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  getFirstItemIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  getLastItemIndex(): number {
    const end = this.currentPage * this.itemsPerPage;
    return end > this.previewData.length ? this.previewData.length : end;
  }

  getPages(): number[] {
    const pages: number[] = [];
    let startPage = Math.max(1, this.currentPage - Math.floor(this.visiblePages / 2));
    const endPage = Math.min(this.totalPages, startPage + this.visiblePages - 1);

    startPage = Math.max(1, endPage - this.visiblePages + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  backToMapping() {
    this.showPreview = false;
    this.currentPage = 1;
    this.totalPages = 1;
  }

  finalizeImport() {
    this.activeModal.close(this.previewData);
  }

  processData() {
    return this.rawData.map((row: any[]) => {
      const product: any = {};
      this.modelKeys.forEach(key => {
        const column = this.columnMapping[key.title];
        const index = this.headers.indexOf(column);
        product[key.title] = row[index];
      });
      return product;
    });
  }
}
